import {
  type GetWorkspaceAssetsArgs,
  type GetWorkspaceFoldersArgs,
  type SearchResult,
  useInfiniteScrollFromCache,
  useLazyGetArchivedWorkspaceFoldersQuery,
  useLazyGetWorkspaceAssetsQuery,
} from '@carboncopyco/data';
import type { Entity } from '@carboncopyco/types';
import { SkeletonImageLoading } from '@carboncopyco/ui';
import { useEffect, useMemo, useRef } from 'react';
import { InfiniteList } from '../../InfiniteList';
import { useUrlState } from '../../utils';
import { AssetGridSection } from './AssetGridSection';
import { WorkspacePageArchiveEmptyState } from './WorkspacePageArchiveEmptyState';
import { useWorkspacePageProps } from './WorkspacePageContext';
import { WorkspacePageControls } from './WorkspacePageControls';
import { WorkspacePageEmptySearch } from './WorkspacePageEmptySearch';
import { WorkspacePageFolderGrid } from './WorkspacePageFolderGrid';
import { WorkspacePageTableView } from './WorkspacePageTableView';
import { ALL_ENTITIES, INITIAL_URL_STATE } from './consts';
import type { UrlState } from './types';

type DirtyRef = {
  isDirty: boolean;
  resetControls: () => void;
};

export const WorkspacePageBodyArchived = () => {
  const { workspaceId } = useWorkspacePageProps();
  const [urlState] = useUrlState<UrlState>(INITIAL_URL_STATE);
  const { viewMode, entities, creators, query, sort } = urlState;
  const dirtyRef = useRef<DirtyRef>({
    isDirty: false,
    resetControls: () => undefined,
  });
  const isDirty = dirtyRef?.current?.isDirty;

  const { organizationId } = useWorkspacePageProps();

  const fetchArgs = useMemo<GetWorkspaceAssetsArgs>(
    () => ({
      organizationId,
      workspaceId,
      creators: creators ? creators?.split(',') : undefined,
      sort,
      deletedEntities: true,
      entities: entities
        ? (entities.split(',') as Entity[])
        : (ALL_ENTITIES as Entity[]),
      query: query && query,
    }),
    [workspaceId, organizationId, entities, creators, query, sort]
  );
  const fetchFolderArgs = useMemo<GetWorkspaceFoldersArgs>(
    () => ({
      organizationId,
      workspaceId,
      creators: creators ? creators?.split(',') : undefined,
      sort,
      entities: entities ? (entities.split(',') as Entity[]) : [],
      query: query && query,
      limit: 100,
    }),
    [workspaceId, organizationId, entities, creators, query, sort]
  );

  const [
    getArchivedFolders,
    {
      data: archivedFolders,
      isLoading: isLoadingArchivedFolders,
      isFetching: isFetchingArchivedFolders,
      isSuccess: isSuccessArchivedFolders,
    },
  ] = useLazyGetArchivedWorkspaceFoldersQuery();

  const {
    items,
    isLoading,
    isLoadingBatch,
    error,
    fetchFirstBatch,
    fetchNextBatch,
    isEmpty: noArchivedAssets,
  } = useInfiniteScrollFromCache<SearchResult, GetWorkspaceAssetsArgs>(
    useLazyGetWorkspaceAssetsQuery,
    fetchArgs
  );

  useEffect(() => {
    fetchFirstBatch();
    getArchivedFolders(fetchFolderArgs);
  }, [fetchArgs, fetchFirstBatch, fetchFolderArgs, getArchivedFolders]);

  const hasArchivedFolders = archivedFolders?.length > 0;
  const noArchivedAssetNoFolder = noArchivedAssets && !hasArchivedFolders;
  const wsArchiveIsEmpty = noArchivedAssetNoFolder && !isDirty;
  const searchIsEmpty = isDirty && noArchivedAssetNoFolder;

  if (wsArchiveIsEmpty && !isLoadingArchivedFolders && !isLoading) {
    return <WorkspacePageArchiveEmptyState />;
  }

  return (
    <>
      <WorkspacePageControls
        ref={dirtyRef}
        archive
      />
      {searchIsEmpty && isSuccessArchivedFolders ? (
        <WorkspacePageEmptySearch
          resetControls={dirtyRef.current.resetControls}
        />
      ) : (
        <div className="w-full flex justify-center overflow-y-hidden flex-auto py-5">
          <div className="w-ds-page h-full flex overflow-y-hidden relative">
            {viewMode === 'tiles' ? (
              <InfiniteList
                error={
                  error && {
                    message: 'Error fetching assets',
                  }
                }
                isLoading={
                  isLoading ||
                  isLoadingArchivedFolders ||
                  isFetchingArchivedFolders
                }
                isLoadingBatch={isLoadingBatch}
                loader={
                  <SkeletonImageLoading
                    cols={4}
                    rows={4}
                  />
                }
                onReachBottom={fetchNextBatch}
              >
                {hasArchivedFolders && (
                  <WorkspacePageFolderGrid
                    data={archivedFolders}
                    isArchived
                  />
                )}
                <AssetGridSection
                  items={items}
                  archived
                />
              </InfiniteList>
            ) : (
              <WorkspacePageTableView
                items={items || ([] as SearchResult[])}
                folders={archivedFolders}
                isLoading={isLoading}
                isLoadingBatch={isLoadingBatch}
                error={error}
                fetchNextBatch={fetchNextBatch}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};
